import { Avatar, Dropdown } from '@iveco-ti/iveco-ds'
import {
  CircleUser,
  Fingerprint,
  FolderOpen,
  LogOut,
  Settings,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { ROUTES } from '@/common/constants/routes'
import { useActiveGeneralSettings } from '@/common/hooks/useActiveGeneralSettings'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { useSignOut } from '@/common/hooks/useSignOut'
import { translateUserType } from '@/common/utils/translateUserType'
import { colorAvatarByName } from '../utils/colorAvatarByName'
import { Can } from './Can'

export function AvatarMenu() {
  const { userInfo } = useGetUserInfo()
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { signOut } = useSignOut()
  const { settings } = useActiveGeneralSettings()

  return (
    <Dropdown.Root openMode="click" position="bottom-right">
      <Dropdown.Trigger asChild>
        <Avatar
          name={userInfo?.Person?.name}
          src={userInfo?.UserPreference?.avatarUrl || ''}
          size="md"
          type="solid"
          color={colorAvatarByName(userInfo?.Person?.name || '')}
        />
      </Dropdown.Trigger>

      <Dropdown.Body className="border border-base-200 shadow-sm">
        <Dropdown.Header>
          <div className="flex flex-col">
            <span className="font-semibold text-sm">
              {userInfo?.Person?.name}
            </span>
            <span className="text-xs text-gray-500">
              {userInfo && translateUserType(t, userInfo.userType)}
            </span>
          </div>
        </Dropdown.Header>

        <Dropdown.List>
          <Dropdown.Item
            className="text-brand-900"
            onClick={() => navigate(ROUTES.PROFILE.HOME)}
          >
            <Dropdown.Icon icon={<CircleUser />} />
            {t('authenticated_layout.user_dropdown.profile')}
          </Dropdown.Item>

          <Can I="access" a="Registers">
            <>
              <Dropdown.Divider />
              <Dropdown.Item
                className="text-brand-900"
                onClick={() => navigate(ROUTES.REGISTERS.USERS)}
              >
                <Dropdown.Icon icon={<FolderOpen />} />
                {t('authenticated_layout.user_dropdown.registers')}
              </Dropdown.Item>
            </>
          </Can>

          {settings?.singleSignOn && (
            <Can I="access" a="Sso">
              <>
                <Dropdown.Divider />

                <Dropdown.Item
                  className="text-brand-900"
                  onClick={() => navigate(ROUTES.SSO.ACCESS)}
                >
                  <Dropdown.Icon icon={<Fingerprint />} />
                  {t('authenticated_layout.user_dropdown.sso')}
                </Dropdown.Item>
              </>
            </Can>
          )}

          <Can I="access" a="Settings">
            <>
              <Dropdown.Divider />

              <Dropdown.Item
                className="text-brand-900"
                onClick={() => navigate(ROUTES.SETTINGS.GENERAL)}
              >
                <Dropdown.Icon icon={<Settings />} />
                {t('authenticated_layout.user_dropdown.settings')}
              </Dropdown.Item>
            </>
          </Can>

          <Dropdown.Divider />

          <Dropdown.Item
            className="text-danger-700 hover:bg-danger-100"
            onClick={signOut}
          >
            <Dropdown.Icon icon={<LogOut />} />
            {t('authenticated_layout.user_dropdown.sign_out')}
          </Dropdown.Item>
        </Dropdown.List>
      </Dropdown.Body>
    </Dropdown.Root>
  )
}
